import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { IntakeRenderer } from '@helios/ui/intake';
import type { IntakeQuestionnaire, IntakeAnswers, FieldRenderer } from '@helios/ui/intake';
import { WhatsAppOptInControl } from '../components/WhatsAppOptInControl';

// ─── Types ───

export type EditDrawerMode = 'details' | 'contacts';

interface EditPatientDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  mode?: EditDrawerMode;
  questionnaire: IntakeQuestionnaire;
  initialAnswers: IntakeAnswers;
  validationSchema: string | null;
  onSave: (answers: IntakeAnswers) => void;
  /**
   * PRD.315 — optional pre-save gate. Resolving false aborts the save and
   * leaves the drawer OPEN with the user's entered values intact, so a
   * "Review" decision on the age/DOB conflict prompt returns them to the form
   * they were filling rather than discarding it.
   */
  confirmBeforeSave?: (answers: IntakeAnswers) => Promise<boolean>;
  fieldRenderers?: Record<string, FieldRenderer>;
  /** Patient id — enables the WhatsApp opt-in control in 'contacts' mode (PRD.222). */
  patientId?: string;
}

// ─── Main Component ───

export function EditPatientDetailsDrawer({
  open,
  onOpenChange,
  brandTokens,
  mode = 'details',
  questionnaire,
  initialAnswers,
  validationSchema,
  onSave,
  confirmBeforeSave,
  fieldRenderers,
  patientId,
}: EditPatientDetailsDrawerProps) {
  const [localAnswers, setLocalAnswers] = useState<IntakeAnswers>(initialAnswers);
  const didHydrateRef = useRef(false);

  // Hydrate once on open; don't clobber while user is editing
  useEffect(() => {
    if (open && !didHydrateRef.current) {
      setLocalAnswers(initialAnswers);
      didHydrateRef.current = true;
    }
    if (!open) didHydrateRef.current = false;
  }, [open, initialAnswers]);

  const handleSave = async () => {
    if (confirmBeforeSave && !(await confirmBeforeSave(localAnswers))) return;
    onSave(localAnswers);
    onOpenChange(false);
  };

  const title = mode === 'contacts' ? 'Edit Contact Information' : 'Edit Patient Details';

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={() => void handleSave()}>
            Save Changes
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        <IntakeRenderer
          questionnaire={questionnaire}
          value={localAnswers}
          onChange={setLocalAnswers}
          errors={[]}
          showFieldErrors={false}
          validationSchema={validationSchema}
          fieldRenderers={fieldRenderers}
        />

        {/* WhatsApp opt-in (PRD.222) — manages its own writes + confirm flow,
            independent of the drawer's "Save Changes" button. */}
        {mode === 'contacts' && patientId && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3, 12px)',
              paddingTop: 'var(--sp-4, 16px)',
              borderTop: '1px solid var(--border)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              WhatsApp Reminders
            </span>
            <WhatsAppOptInControl patientId={patientId} />
          </div>
        )}
      </div>
    </Drawer>
  );
}
